import { useState } from "react";
import SearchBar from "../../components/SearchBar";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useToast } from "../../context/ToastContext";
import {
  usePlaceImages,
  useModeratePlaceImages,
} from "../../hooks/usePlaceImages";

const LIMIT = 1000;

// --- Field extraction (pin these once the real response shape is confirmed) ---
const idOf = (img) => img.id ?? img.image_id ?? img.imageId;
const urlOf = (img) =>
  img.imageUrl ??
  img.image_url ??
  img.url ??
  img.image ??
  img.src ??
  img.path ??
  "";
const captionOf = (img) =>
  img.placeName ??
  img.place_name ??
  img.name ??
  img.title ??
  img.place?.name ??
  "";
const sourceOf = (img) => img.source ?? img.source_name ?? "";

export default function ImageModerationPage() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [moderateOpen, setModerateOpen] = useState(false);

  const { data, isLoading, error } = usePlaceImages({ limit: LIMIT, search });
  // Accept both { data:[...] , total,... } envelope and a bare array.
  const rows = Array.isArray(data) ? data : data?.data || [];

  const clearSelection = () => setSelected(new Set());

  const pageIds = rows.map(idOf).filter((id) => id != null);

  // Group images by place name, preserving first-seen order.
  const groups = [];
  const groupIndex = new Map();
  for (const img of rows) {
    const label = captionOf(img) || "Unknown place";
    let g = groupIndex.get(label);
    if (!g) {
      g = { label, images: [] };
      groupIndex.set(label, g);
      groups.push(g);
    }
    g.images.push(img);
  }

  const modM = useModeratePlaceImages({
    onSuccess: () => {
      toast.success(`Deleted ${selected.size} image(s) · moderated ${pageIds.length}`);
      setModerateOpen(false);
      clearSelection();
    },
    onError: (e) => toast.error(e.message || "Moderation failed"),
  });

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(pageIds));

  const runModerate = () =>
    modM.mutate({ deleteIds: Array.from(selected), allIds: pageIds });

  const bulkBar = rows.length > 0 && (
    <div className="bulk-bar">
      <label className="bulk-bar__all">
        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
        <span>Select all ({pageIds.length})</span>
      </label>
      <span className="bulk-bar__count">{selected.size} selected</span>
      <button
        className="btn btn--sm btn--danger"
        disabled={selected.size === 0}
        onClick={() => setModerateOpen(true)}
      >
        Moderate
      </button>
      {selected.size > 0 && (
        <button className="btn btn--sm btn--ghost" onClick={clearSelection}>
          Clear
        </button>
      )}
    </div>
  );

  return (
    <div className="page">
      <div className="page__header">
        <h1>Image Moderation</h1>
      </div>
      <div className="page__toolbar">
        <SearchBar
          value={search}
          onSearch={setSearch}
          placeholder="Search images…"
        />
      </div>

      {bulkBar}

      {isLoading ? (
        <div className="state state--loading">Loading…</div>
      ) : error ? (
        <div className="state state--error" role="alert">
          {error.message || "Failed to load images."}
        </div>
      ) : rows.length === 0 ? (
        <div className="state state--empty">No images found.</div>
      ) : (
        groups.map((group) => (
          <section className="img-group" key={group.label}>
            <h2 className="img-group__title">
              {group.label}
              <span className="img-group__count">{group.images.length}</span>
            </h2>
            <div className="img-grid">
              {group.images.map((img) => {
                const id = idOf(img);
                const url = urlOf(img);
                const caption = group.label;
                const source = sourceOf(img);
                const checked = selected.has(id);
                return (
                  <div
                    className={`img-card${checked ? " img-card--selected" : ""}`}
                    key={id ?? url}
                  >
                    <label className="img-card__check">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(id)}
                      />
                    </label>
                    <div
                      className="img-card__thumb"
                      role="button"
                      tabIndex={0}
                      aria-pressed={checked}
                      onClick={() => toggle(id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle(id);
                        }
                      }}
                    >
                      {url ? (
                        <img
                          src={url}
                          alt={caption || `image ${id ?? ""}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="img-card__missing">no image url</div>
                      )}
                      {source && <span className="img-card__source">{source}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {bulkBar}

      <ConfirmDialog
        open={moderateOpen}
        title="Moderate images?"
        message={`Delete ${selected.size} selected image(s) and mark all ${pageIds.length} as moderated? This cannot be undone.`}
        confirmLabel="Moderate"
        pending={modM.isPending}
        onCancel={() => setModerateOpen(false)}
        onConfirm={runModerate}
      />
    </div>
  );
}
